import React, { useEffect, useMemo, useState } from "react";
import styles from "./ReservationHistory.module.css";
import {
  apiGetReservationHistory,
  IReservationHistoryItem,
} from "../../../services/GET/getReservationHistory.service";

interface ReservationHistoryProps {
  reserveId: string | number;
}

const TYPE_LABELS: Record<string, string> = {
  creacion: "Creación",
  estado: "Estado",
  empleado: "Estilista",
  fechahora: "Fecha y hora",
  servicio: "Servicio",
  cliente: "Cliente",
  precio: "Precio",
  notas: "Notas",
  cancelacion: "Cancelación",
  en_comanda: "En comanda",
  auto_por_asignar: "Reasignación automática",
};

// Color del punto del timeline por tipo de evento.
const TYPE_COLORS: Record<string, string> = {
  creacion: "#0ea5e9",
  estado: "#f59e0b",
  empleado: "#8b5cf6",
  fechahora: "#6366f1",
  servicio: "#ec4899",
  cliente: "#14b8a6",
  precio: "#f97316",
  notas: "#64748b",
  cancelacion: "#ef4444",
  en_comanda: "#22c55e",
  auto_por_asignar: "#a855f7",
};

const getTypeLabel = (type: string): string => TYPE_LABELS[type] || "Cambio";
const getTypeColor = (type: string): string => TYPE_COLORS[type] || "#937cf4";

const authorLabel = (item: IReservationHistoryItem): string =>
  item.isSystem ? "Sistema" : item.authorName || "Desconocido";

const formatAbsolute = (date: string): string => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleString("es-PE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatRelative = (date: string): string => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "";
  const diffMs = Date.now() - parsed.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "hace instantes";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `hace ${d} d`;
  return formatAbsolute(date);
};

// Etiqueta del grupo por día: Hoy / Ayer / dd/mm/yyyy.
const dayKey = (date: string): string => {
  const d = new Date(date);
  return Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString("es-PE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const dayLabel = (date: string): string => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const k = dayKey(date);
  if (k === dayKey(today.toISOString())) return "Hoy";
  if (k === dayKey(yesterday.toISOString())) return "Ayer";
  return k;
};

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="#5b21b6" strokeWidth="2" />
    <path d="M12 7v5l3 2" stroke="#5b21b6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const EventItem: React.FC<{ item: IReservationHistoryItem }> = ({ item }) => (
  <li className={styles.event}>
    <span
      className={styles.dot}
      style={{ background: getTypeColor(item.type) }}
      aria-hidden="true"
    />
    <div className={styles.eventBody}>
      <div className={styles.eventHeader}>
        <span className={styles.typeTag}>{getTypeLabel(item.type)}</span>
        <time className={styles.date}>{formatAbsolute(item.date)}</time>
      </div>
      <p className={styles.description}>{item.description}</p>
      {(item.oldValue !== null || item.newValue !== null) && (
        <p className={styles.change}>
          {item.oldValue && (
            <span className={styles.oldValue}>{item.oldValue}</span>
          )}
          {item.oldValue && item.newValue && (
            <span className={styles.arrow}>→</span>
          )}
          {item.newValue && (
            <span className={styles.newValue}>{item.newValue}</span>
          )}
        </p>
      )}
      <span className={styles.author}>{authorLabel(item)}</span>
    </div>
  </li>
);

const ReservationHistory: React.FC<ReservationHistoryProps> = ({ reserveId }) => {
  const [items, setItems] = useState<IReservationHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (reserveId === null || reserveId === undefined || reserveId === "") return;

    let cancelled = false;
    const fetchHistory = async () => {
      setLoading(true);
      setError("");
      const response = await apiGetReservationHistory(reserveId);
      if (cancelled) return;
      if (response.status === 200) {
        setItems(Array.isArray(response.data) ? response.data : []);
      } else {
        setError(response.message || "Error al obtener el historial");
        setItems([]);
      }
      setLoading(false);
    };
    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [reserveId]);

  // Cierra el sidebar con Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Agrupa los eventos por día para el sidebar (ya vienen ordenados DESC).
  const groups = useMemo(() => {
    const map = new Map<string, IReservationHistoryItem[]>();
    for (const item of items) {
      const label = dayLabel(item.date);
      const arr = map.get(label) || [];
      arr.push(item);
      map.set(label, arr);
    }
    return Array.from(map.entries()).map(([label, groupItems]) => ({
      label,
      items: groupItems,
    }));
  }, [items]);

  const latest = items[0];

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <ClockIcon />
        <span className={styles.panelTitle}>Historial de cambios</span>
        {items.length > 0 && (
          <span className={styles.countBadge}>{items.length}</span>
        )}
      </div>

      {loading && <p className={styles.stateMessage}>Cargando historial...</p>}

      {!loading && error && <p className={styles.errorMessage}>{error}</p>}

      {!loading && !error && items.length === 0 && (
        <p className={styles.stateMessage}>Sin cambios registrados</p>
      )}

      {!loading && !error && latest && (
        <>
          <div className={styles.latestCard}>
            <div className={styles.latestTop}>
              <span className={styles.typeTag}>{getTypeLabel(latest.type)}</span>
              <time className={styles.relativeDate}>
                {formatRelative(latest.date)}
              </time>
            </div>
            <p className={styles.latestDesc}>{latest.description}</p>
            <span className={styles.author}>{authorLabel(latest)}</span>
          </div>

          <button
            type="button"
            className={styles.fullBtn}
            onClick={() => setOpen(true)}
          >
            Ver historial completo ›
          </button>
        </>
      )}

      {open && (
        <>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <aside
            className={styles.sidebar}
            role="dialog"
            aria-label="Historial de cambios"
          >
            <header className={styles.sidebarHeader}>
              <span className={styles.sidebarTitle}>
                <ClockIcon />
                Historial de cambios
              </span>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => setOpen(false)}
                aria-label="Cerrar"
              >
                <CloseIcon />
              </button>
            </header>

            <div className={styles.sidebarBody}>
              {groups.map((group) => (
                <div key={group.label} className={styles.dayGroup}>
                  <p className={styles.dayLabel}>{group.label}</p>
                  <ul className={styles.timeline}>
                    {group.items.map((item) => (
                      <EventItem key={item.id} item={item} />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default ReservationHistory;
